import { Component, OnInit, inject } from '@angular/core';
import { LayoutService } from '../services/layout.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ThemeService, Theme } from '../services/theme.service';
import { AuthService } from '../services/auth.service';
import { User, UserRole } from '../models/user.model';
import { StockSearchComponent, SearchResult } from '../components/stock-search.component';
import { WatchlistPopoverComponent } from './watchlist-popover.component';
import { EnvBadgeComponent } from './env-badge.component';
import { computeMarketSession, MarketSession } from '../shared/market-clock';

@Component({
    selector: 'app-topbar',
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        PopoverModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        TagModule,
        StockSearchComponent,
        WatchlistPopoverComponent,
        EnvBadgeComponent,
    ],
    template: `
    <div class="topbar">
      <div class="topbar-left">
        <button
          pButton
          type="button"
          pRipple
          icon="pi pi-bars"
          class="p-button-rounded p-button-text p-button-plain"
          (click)="toggleSidebar()"
          aria-label="Toggle Sidebar"
          data-testid="sidebar-toggle"
        ></button>
        <a routerLink="/" class="app-logo" aria-label="AlphaGroves home">
          <img
            [src]="currentTheme === 'dark'
              ? 'assets/alphagroves-brand-kit/lockup/lockup-reversed-dark.png'
              : 'assets/alphagroves-brand-kit/lockup/lockup-primary.png'"
            alt="AlphaGroves"
            class="app-logo-img"
          />
        </a>
        <!-- SCRUM-850 — environment badge (hidden on prod). -->
        <app-env-badge></app-env-badge>

        <!-- SCRUM-1975 — today's date + US market session, moved here from the
             Morning Brief header so the brief clears the fold. Date hides on
             tablet/phone; the status badge stays. -->
        <div class="tb-market-clock" data-testid="topbar-market-clock">
          <span class="tb-date">{{ todayLabel }}</span>
          <span class="tb-market-status"
                [ngClass]="'tb-status-' + marketSession.status"
                data-testid="market-status">{{ marketSession.label }}</span>
        </div>
      </div>

      <div class="topbar-center">
        <app-stock-search [showActiveContext]="true" (symbolSelected)="onSymbolSelected($event)"></app-stock-search>
      </div>

      <div class="topbar-right">
        <!-- Mobile Search Toggle (only visible on mobile) -->
        <button
          pButton
          type="button"
          pRipple
          icon="pi pi-search"
          class="p-button-rounded p-button-text p-button-plain mobile-search-btn"
          (click)="showMobileSearch()"
          aria-label="Open search"
        ></button>

        <!-- User Role Badge -->
        <p-tag
          *ngIf="currentUser"
          [value]="getRoleLabel()"
          [severity]="getRoleSeverity()"
          class="role-badge"
        ></p-tag>

        <!-- SCRUM-2297 — always-visible Car mode toggle. The only manual switch
             used to be buried in the avatar menu; a driver needs it one tap away,
             especially since auto-detect can miss on current Tesla firmware.
             Highlighted when active. Flips live, no reload. -->
        <button
          pButton
          type="button"
          pRipple
          icon="pi pi-car"
          class="p-button-rounded p-button-text p-button-plain car-mode-btn"
          [class.car-mode-active]="carMode"
          (click)="toggleCarMode()"
          [attr.aria-label]="carMode ? 'Turn car mode off' : 'Turn car mode on'"
          [attr.aria-pressed]="carMode"
          [title]="carMode ? 'Car mode: On (large in-vehicle text)' : 'Car mode: Off'"
          data-testid="car-mode-toggle-topbar"
        ></button>

        <!-- Watchlist quick-manage popover (sits next to the avatar).
             SCRUM-813 — deferred (on idle) because this component drags in
             a heavy chunk of PrimeNG (Popover, ConfirmDialog, Toast, Skeleton,
             Tooltip, InputText, Button) plus FormsModule, easily 80+ kB of
             eager code that nobody needs before first paint. -->
        @defer (on idle) {
          <app-watchlist-popover *ngIf="currentUser"></app-watchlist-popover>
        }

        <!-- User Menu -->
        <button
          #userMenuBtn
          pButton
          type="button"
          pRipple
          (click)="op.toggle($event)"
          class="p-button-rounded p-button-text p-button-plain user-menu-btn"
          [attr.aria-label]="currentUser ? currentUser.displayName : 'User Menu'"
        >
          <ng-container *ngIf="avatarUrl; else userIcon">
            <img [src]="avatarUrl" class="user-avatar-img" alt="avatar"
                 (error)="onAvatarError()" />
          </ng-container>
          <ng-template #userIcon><i class="pi pi-user"></i></ng-template>
          <span class="user-name" *ngIf="firstName && !avatarUrl">{{ firstName }}</span>
        </button>
        <p-popover #op styleClass="user-menu-popup" >
          <div class="um-panel">
            <!-- Header: identity -->
            <div class="um-header">
              <div class="um-avatar-wrap">
                <img *ngIf="avatarUrl; else umIcon" [src]="avatarUrl" class="um-avatar" alt="avatar"
                     (error)="onAvatarError()" />
                <ng-template #umIcon><div class="um-avatar um-avatar-fallback"><i class="pi pi-user"></i></div></ng-template>
              </div>
              <div class="um-identity">
                <div class="um-name">{{ currentUser?.displayName || 'User' }}</div>
                <div class="um-email" *ngIf="currentUser?.email">{{ currentUser?.email }}</div>
                <p-tag *ngIf="currentUser"
                       [value]="getRoleLabel()"
                       [severity]="getRoleSeverity()"
                       class="um-role-tag"></p-tag>
              </div>
            </div>

            <!-- Account section -->
            <div class="um-section">
              <a class="um-item" routerLink="/pages/profile" (click)="op.hide()">
                <i class="pi pi-user-edit um-icon"></i>
                <span class="um-label">Profile</span>
              </a>
            </div>

            <!-- Preferences section -->
            <div class="um-section">
              <button type="button" class="um-item um-item-button" (click)="toggleTheme()">
                <i class="pi um-icon" [ngClass]="currentTheme === 'light' ? 'pi-moon' : 'pi-sun'"></i>
                <span class="um-label">{{ currentTheme === 'light' ? 'Dark mode' : 'Light mode' }}</span>
                <span class="um-toggle-state">{{ currentTheme === 'light' ? 'Light' : 'Dark' }}</span>
              </button>
              <!-- SCRUM-1981 — manual Car mode override (large in-vehicle text).
                   Auto-detected from the Tesla UA in main.ts, but exposed here so
                   it can be forced on/off on any device. Flips live, no reload. -->
              <button type="button" class="um-item um-item-button" (click)="toggleCarMode()"
                      data-testid="car-mode-toggle">
                <i class="pi pi-car um-icon"></i>
                <span class="um-label">Car mode</span>
                <span class="um-toggle-state">{{ carMode ? 'On' : 'Off' }}</span>
              </button>
            </div>

            <!-- Admin section -->
            <div class="um-section" *ngIf="currentUser?.role === 'admin'">
              <a class="um-item" routerLink="/admin/status" (click)="op.hide()">
                <i class="pi pi-shield um-icon"></i>
                <span class="um-label">Admin Console</span>
              </a>
            </div>

            <!-- Source repos (SCRUM-1210 post-split: 3 sibling Bitbucket repos). -->
            <!-- Sign in to Bitbucket first — these repos are private, so unauth-      -->
            <!-- enticated visits return 404. Titles list the primary purpose so       -->
            <!-- admins know which repo owns which surface without opening each.       -->
            <div class="um-section" *ngIf="currentUser?.role === 'admin'">
              <a class="um-item" href="https://bitbucket.org/hmanai/hub-financial"
                 target="_blank" rel="noopener noreferrer" (click)="op.hide()"
                 title="hub-financial — this app (Angular frontend + 17 backend services + Playwright tests). Requires Bitbucket login.">
                <i class="pi pi-code um-icon"></i>
                <span class="um-label">hub-financial · Trading app</span>
                <i class="pi pi-external-link um-ext-icon"></i>
              </a>
              <a class="um-item" href="https://bitbucket.org/hmanai/hub-platform"
                 target="_blank" rel="noopener noreferrer" (click)="op.hide()"
                 title="hub-platform — Ops Plane + shared infra (Jenkins, SonarQube, registry, Keycloak, Vault, prod-agent, observability). Requires Bitbucket login.">
                <i class="pi pi-server um-icon"></i>
                <span class="um-label">hub-platform · Ops &amp; infra</span>
                <i class="pi pi-external-link um-ext-icon"></i>
              </a>
              <a class="um-item" href="https://bitbucket.org/hmanai/inara-workspace"
                 target="_blank" rel="noopener noreferrer" (click)="op.hide()"
                 title="inara-workspace — Agent personas, memory, and inara-api Fastify service. Requires Bitbucket login.">
                <i class="pi pi-book um-icon"></i>
                <span class="um-label">inara-workspace · Agents</span>
                <i class="pi pi-external-link um-ext-icon"></i>
              </a>
            </div>

            <!-- Sign out -->
            <div class="um-section um-section-signout">
              <button type="button" class="um-item um-signout" (click)="logout(); op.hide()">
                <i class="pi pi-sign-out um-icon"></i>
                <span class="um-label">Sign out</span>
              </button>
            </div>
          </div>
        </p-popover>
      </div>

      <!-- MOBILE_UX_SPEC Story 1 — mobile quick-actions bar replaced by
           bottom-nav.component (see <app-bottom-nav> in app-layout). -->
    </div>

    <!-- Mobile search overlay -->
    <div
      class="mobile-search-backdrop"
      *ngIf="mobileSearchOpen"
      (click)="hideMobileSearch()"
    ></div>
    <div class="mobile-search-panel" [class.open]="mobileSearchOpen">
      <app-stock-search
        (symbolSelected)="onMobileSymbolSelected($event)"
      ></app-stock-search>
      <button
        pButton
        type="button"
        pRipple
        icon="pi pi-times"
        class="p-button-rounded p-button-text p-button-plain mobile-search-close"
        (click)="hideMobileSearch()"
        aria-label="Close search"
      ></button>
    </div>
  `,
    styles: [`
    :host {
      display: block;
    }

    .topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 1.5rem;
      /* viewport-fit=cover: expand by safe-area-inset-top so content clears the notch */
      padding-top: env(safe-area-inset-top, 0px);
      height: 70px;
      height: calc(70px + env(safe-area-inset-top, 0px));
      background: var(--surface-0);
      border-bottom: 1px solid var(--surface-border);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
    }

    .topbar-left {
      display: flex;
      align-items: center;
      gap: 1rem;
      min-width: 0;
    }

    .app-logo {
      display: flex;
      align-items: center;
      text-decoration: none;
      user-select: none;
      flex-shrink: 0;
    }

    .app-logo-img {
      height: 32px;
      width: auto;
      display: block;
    }

    @media screen and (max-width: 576px) {
      .app-logo-img {
        height: 26px;
      }
    }

    .topbar-center {
      flex: 1;
      display: flex;
      justify-content: center;
      max-width: 400px;
      margin: 0 auto;
    }

    .input-group {
      display: flex;
      width: 100%;
      max-width: 300px;
    }

    .input-group-addon {
      display: flex;
      align-items: center;
      padding: 0 0.75rem;
      background: var(--surface-50);
      border: 1px solid var(--surface-border);
      border-right: none;
      border-radius: 6px 0 0 6px;
      color: var(--text-color-secondary);
    }

    .topbar-search {
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--surface-border);
      border-left: none;
      border-radius: 0 6px 6px 0;
      background: var(--surface-50);
      color: var(--text-color);
      font-size: 0.875rem;
    }

    .topbar-search:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px var(--primary-color, rgba(59, 130, 246, 0.1));
    }

    .topbar-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    .user-menu-btn {
      cursor: pointer;
    }

    /* SCRUM-2297 — highlight the car-mode button when active so its state is
       obvious at a glance. */
    .car-mode-btn.car-mode-active {
      color: var(--primary-color);
      background: var(--primary-50, rgba(11, 106, 67, 0.14));
    }

    .user-avatar-img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid var(--surface-border);
      display: block;
    }

    /* ─── User menu popup (PrimeNG Popover) ─────────────────────── */
    ::ng-deep .user-menu-popup.p-overlaypanel {
      min-width: 280px;
      max-width: 320px;
      border: 1px solid var(--surface-border);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      border-radius: 12px;
      overflow: hidden;
    }
    ::ng-deep .user-menu-popup.p-overlaypanel .p-overlaypanel-content {
      padding: 0;
    }
    ::ng-deep .user-menu-popup.p-overlaypanel:before,
    ::ng-deep .user-menu-popup.p-overlaypanel:after {
      display: none;
    }

    .um-panel { display: flex; flex-direction: column; }

    /* Header */
    .um-header {
      display: flex; gap: 0.75rem; align-items: flex-start;
      padding: 1rem;
      background: var(--surface-50);
      border-bottom: 1px solid var(--surface-border);
    }
    .um-avatar-wrap { flex-shrink: 0; }
    .um-avatar {
      width: 44px; height: 44px;
      border-radius: 50%; object-fit: cover;
      border: 2px solid var(--surface-border);
      display: block;
    }
    .um-avatar-fallback {
      display: flex; align-items: center; justify-content: center;
      background: var(--primary-color); color: white;
      font-size: 1.1rem;
    }
    .um-identity { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 2px; }
    .um-name {
      font-weight: 600; color: var(--text-color);
      font-size: 0.95rem; line-height: 1.2;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .um-email {
      font-size: 0.8rem; color: var(--text-color-secondary);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .um-role-tag {
      align-self: flex-start;
      margin-top: 4px;
      font-size: 0.7rem;
    }

    /* Sections */
    .um-section {
      display: flex; flex-direction: column;
      padding: 0.35rem 0;
      border-bottom: 1px solid var(--surface-border);
    }
    .um-section:last-child { border-bottom: none; }
    .um-section-signout { padding-bottom: 0.5rem; padding-top: 0.5rem; }

    /* Items */
    .um-item {
      display: flex; align-items: center; gap: 0.65rem;
      padding: 0.6rem 1rem;
      color: var(--text-color);
      font-size: 0.9rem;
      text-decoration: none;
      background: transparent; border: none; width: 100%;
      cursor: pointer; text-align: left;
      transition: background-color 0.12s;
    }
    .um-item:hover,
    .um-item:focus-visible {
      background: var(--surface-hover);
      outline: none;
    }
    .um-icon {
      flex-shrink: 0;
      width: 1.1rem; font-size: 0.95rem;
      color: var(--text-color-secondary);
    }
    .um-ext-icon {
      flex-shrink: 0;
      font-size: 0.72rem;
      color: var(--text-color-secondary);
      opacity: 0.6;
    }
    .um-label { flex: 1; }
    .um-toggle-state {
      font-size: 0.72rem; padding: 2px 8px;
      background: var(--surface-100); color: var(--text-color-secondary);
      border-radius: 999px; font-weight: 500;
      text-transform: uppercase; letter-spacing: 0.3px;
    }
    .um-item-button { cursor: pointer; }

    /* Sign out — subtle red, only on hover */
    .um-signout { color: var(--text-color); }
    .um-signout:hover,
    .um-signout:focus-visible {
      background: rgba(239, 68, 68, 0.08);
      color: var(--error-color, #ef4444);
    }
    .um-signout:hover .um-icon,
    .um-signout:focus-visible .um-icon { color: var(--error-color, #ef4444); }

    .user-name {
      font-size: 0.8rem;
      font-weight: 600;
      margin-left: 0.25rem;
    }
    @media (max-width: 576px) {
      .user-name { display: none; }
    }

    .role-badge {
      font-size: 0.7rem;
      cursor: default;
    }

    /* SCRUM-1975 — top-bar date + market session badge. */
    .tb-market-clock {
      display: flex; align-items: center; gap: 0.5rem;
      margin-left: 0.5rem; white-space: nowrap; min-width: 0;
    }
    .tb-date { font-size: 0.82rem; color: var(--text-color-secondary); font-weight: 500; }
    .tb-market-status {
      font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase;
      font-size: 0.68rem; padding: 2px 8px; border-radius: 999px;
    }
    .tb-status-open   { background: rgba(16,185,129,0.18); color: var(--success-color); }
    .tb-status-pre    { background: rgba(59,130,246,0.18); color: var(--primary-color); }
    .tb-status-after  { background: rgba(168,85,247,0.18); color: #c084fc; }
    .tb-status-closed { background: var(--surface-100); color: var(--text-color-secondary); }
    /* Protect the tight mobile top bar (SCRUM-1970 lesson): drop the date text
       on tablet/phone, keep the compact status badge. */
    @media (max-width: 768px) {
      .tb-market-clock { margin-left: 0.25rem; }
      .tb-date { display: none; }
    }
    @media (max-width: 576px) {
      .tb-market-clock { gap: 0.3rem; }
    }

    .mobile-search-btn {
      display: none;
    }

    .mobile-search-backdrop {
      display: none;
    }

    .mobile-search-panel {
      display: none;
    }

    @media screen and (max-width: 768px) {
      .topbar {
        padding: 0 1rem;
        padding-top: env(safe-area-inset-top, 0px);
        height: 112px;      /* 60px logo row + 52px search row */
        height: calc(112px + env(safe-area-inset-top, 0px));
        flex-wrap: wrap;
        align-content: flex-start;
        padding-bottom: 0.5rem;
      }

      .topbar-center {
        display: flex;
        order: 3;
        flex: 0 0 100%;
        max-width: 100%;
        padding: 0 0 0.25rem 0;
        margin: 0;
      }

      .topbar-center app-stock-search {
        width: 100%;
        max-width: 100%;
      }

      .topbar-left {
        order: 1;
      }

      .topbar-right {
        order: 2;
      }

      .app-logo span {
        display: none;
      }

      .app-logo {
        font-size: 1.25rem;
      }

      .mobile-search-btn {
        display: none;
      }

      .mobile-search-backdrop {
        display: none;
        position: fixed;
        top: 60px;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.35);
        z-index: 999;
        animation: mobile-search-fade 0.18s ease-out;
      }

      .mobile-search-panel {
        display: none;
        align-items: center;
        gap: 0.5rem;
        position: fixed;
        top: 60px;
        left: 0;
        right: 0;
        padding: 0.75rem 1rem;
        background: var(--surface-0);
        border-bottom: 1px solid var(--surface-border);
        box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.12));
        z-index: 1001;
        transform: translateY(-12px);
        opacity: 0;
        pointer-events: none;
        transition: transform 0.18s ease-out, opacity 0.18s ease-out;
      }

      .mobile-search-panel.open {
        transform: translateY(0);
        opacity: 1;
        pointer-events: auto;
      }

      .mobile-search-panel app-stock-search {
        flex: 1;
        min-width: 0;
      }

      .mobile-search-close {
        flex-shrink: 0;
      }
    }

    @keyframes mobile-search-fade {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @media screen and (max-width: 576px) {
      .mobile-search-backdrop,
      .mobile-search-panel {
        top: 56px;
      }
    }

    @media screen and (max-width: 576px) {
      .topbar {
        padding: 0 0.75rem;
        height: 108px;      /* 56px logo row + 52px search row */
        flex-wrap: wrap;
        align-content: flex-start;
      }

      .app-logo {
        font-size: 1rem;
        gap: 0.25rem;
      }

      /* 2026-06-17 — iPhone topbar was rendering on 2 rows because the
         topbar-left (hamburger + 26px logo + env-badge) + topbar-right
         (Admin role-badge + watchlist + 32px avatar) collectively
         overflowed 375px. Three tweaks that together get everything onto
         one row:
           - shrink the lockup logo image to 22px on phones
           - tighten gaps in both -left and -right
           - hide the redundant role-badge — the role is also shown
             prominently inside the user menu popup. */
      .app-logo-img { height: 22px; }
      .topbar-left  { gap: 0.5rem; }
      .topbar-right { gap: 0.4rem; }
      .role-badge   { display: none; }
    }

    ::ng-deep .input-group {
      display: flex !important;
    }
  `]
})
export class TopbarComponent implements OnInit {
  /** Hamburger goes straight to the LayoutService — no @Output bounce. */
  private readonly layout = inject(LayoutService);

  currentTheme: Theme = 'light';
  // SCRUM-1981 — car-mode override state, mirrored from the `tesla` class that
  // main.ts sets pre-boot (UA auto-detect or persisted `hub_car_mode` flag).
  carMode = false;
  currentUser: User | null = null;
  firstName = '';
  avatarUrl: string | null = null;
  mobileSearchOpen = false;

  // SCRUM-1975 — date + US market session shown in the top bar.
  todayLabel = '';
  marketSession: MarketSession = computeMarketSession();

  // Note: the user menu is now rendered inline via <p-popover> in the
  // template. Previously this class held a `userMenuItems: MenuItem[]` plus
  // a `buildUserMenu()` rebuild method for the old <p-menu> popup; both
  // were removed when the menu became declarative HTML.

  constructor(
    private readonly themeService: ThemeService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {}

  ngOnInit(): void {
    this.themeService.getTheme().subscribe((theme) => {
      this.currentTheme = theme;
      this.updatePrimeNGTheme(theme);
    });

    // Subscribe to current user for role badge + menu identity header.
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.firstName = this.authService.getFirstName();
    });
    this.firstName = this.authService.getFirstName();
    this.loadAvatar();

    // SCRUM-1981 — reflect the current car-mode state in the menu toggle.
    this.carMode = document.documentElement.classList.contains('tesla');

    // SCRUM-1975 — compact date (e.g. "Wed, Aug 19") + live market session.
    this.todayLabel = new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    }).format(new Date());
    this.marketSession = computeMarketSession();
  }

  loadAvatar(): void {
    // Skip if unauthenticated — avoids a 401 network error logged by the browser
    if (!this.authService.getAccessToken()) return;
    this.http.get<any>('/api/auth/profile').subscribe({
      next: (profile) => {
        if (profile?.avatarUrl) {
          this.avatarUrl = profile.avatarUrl;
        }
      },
      error: () => { /* fail silently — falls back to pi-user icon */ }
    });
  }

  getRoleLabel(): string {
    if (!this.currentUser) return '';
    switch (this.currentUser.role) {
      case UserRole.ADMIN: return 'Admin';
      case UserRole.INVESTOR: return 'Investor';
      default: return this.currentUser.role;
    }
  }

  getRoleSeverity(): 'success' | 'info' | 'warn' | 'danger' | undefined {
    if (!this.currentUser) return undefined;
    switch (this.currentUser.role) {
      case UserRole.ADMIN: return 'danger';
      case UserRole.INVESTOR: return 'success';
      default: return 'info';
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  // SCRUM-1981 — flip the large in-vehicle car mode on/off. Persist the choice
  // in localStorage['hub_car_mode'] (the same flag main.ts reads on boot) AND
  // add/remove the `tesla` class on <html> immediately so the :root.tesla
  // stylesheet takes effect live, with no page reload. Presentation only.
  toggleCarMode(): void {
    this.carMode = !this.carMode;
    const root = document.documentElement;
    try {
      if (this.carMode) {
        localStorage.setItem('hub_car_mode', '1');
        root.classList.add('tesla');
      } else {
        localStorage.removeItem('hub_car_mode');
        root.classList.remove('tesla');
      }
    } catch {
      // localStorage unavailable (private mode / sandbox) — still toggle the
      // class so the current session reflects the choice.
      root.classList.toggle('tesla', this.carMode);
    }
  }

  toggleSidebar(): void {
    this.layout.dbg('hamburger TAP');
    this.layout.toggleSidebar();
  }

  onSymbolSelected(result: SearchResult): void {
    // Top search jumps to the AI Options Analysis dashboard. The dashboard
    // reads ?symbol= from the URL and pre-fills the ticker (and auto-runs).
    this.router.navigate(['/options-analysis'], {
      queryParams: { symbol: result.symbol },
    });
  }

  onMobileSymbolSelected(result: SearchResult): void {
    this.onSymbolSelected(result);
    this.hideMobileSearch();
  }

  // Avatar failed to load (e.g. media 404/403) — fall back to the user icon.
  // Deferred out of the current change-detection pass so mutating avatarUrl
  // does not throw NG0100 (ExpressionChangedAfterItHasBeenChecked) in dev mode.
  onAvatarError(): void {
    Promise.resolve().then(() => { this.avatarUrl = null; });
  }

  showMobileSearch(): void {
    this.mobileSearchOpen = true;
  }

  hideMobileSearch(): void {
    this.mobileSearchOpen = false;
  }

  private updatePrimeNGTheme(_theme: Theme): void {
    // PrimeNG 21 removed PrimeNGConfig; ripple is enabled via providePrimeNG()
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }
}
